import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { 
    Voicemail, 
    MessageSquare,
    AudioLines,
    Info,
    AlertCircle,
    FileAudio,
    Lightbulb
} from "lucide-react";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from "@/components/ui/card";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { SwitchWithLabel } from "@/components/ui/switch-with-label";
import {
    Alert,
    AlertDescription,
} from "@/components/ui/alert";

interface VoicemailFormState {
    messageContent: string;
    useAudioFile: boolean;
}

interface VoicemailFormProps {
    value: VoicemailFormState;
    onChange: (data: VoicemailFormState) => void;
    isReadOnly?: boolean;
}

export function VoicemailForm({ value, onChange, isReadOnly = false }: VoicemailFormProps) {
    const [formData, setFormData] = useState<VoicemailFormState>(value);

    // Memoize the update function
    const updateParent = useCallback((newData: VoicemailFormState) => {
        const isEqual = JSON.stringify(newData) === JSON.stringify(value);
        if (!isEqual) {
            onChange(newData);
        }
    }, [value, onChange]);

    // Update local state when value prop changes
    useEffect(() => {
        const isEqual = JSON.stringify(formData) === JSON.stringify(value);
        if (!isEqual) {
            setFormData(value);
        }
    }, [value]);

    const handleMessageContentChange = (newContent: string) => {
        const newData = { ...formData, messageContent: newContent };
        setFormData(newData);
        updateParent(newData);
    };

    const handleUseAudioFileChange = (checked: boolean) => {
        const newData = { ...formData, useAudioFile: checked };
        setFormData(newData);
        updateParent(newData);
    };

    // Determine if the content looks like a URL
    const isValidUrl = (str: string) => {
        try {
            const url = new URL(str);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
            return false;
        }
    };

    const contentIsUrl = isValidUrl(formData.messageContent);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
        >
            {/* Voicemail Configuration Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Voicemail className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Voicemail Configuration</CardTitle>
                            <CardDescription>
                                Configure the message left when voicemail is detected
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Info Banner */}
                    <div className="rounded-lg border bg-muted/30 p-4">
                        <div className="flex gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                                <Info className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <p className="text-sm font-medium">How Voicemail Detection Works</p>
                                <p className="text-xs text-muted-foreground">
                                    Your assistant automatically detects voicemail indicators (e.g., "unavailable", "leave a message") 
                                    and leaves your configured message before ending the call.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Message Type Toggle */}
                    <div className="space-y-2">
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <SwitchWithLabel
                                            label="Use Pre-recorded Audio File"
                                            description={
                                                formData.useAudioFile 
                                                    ? "Pre-recorded audio ensures consistent pronunciation and quality"
                                                    : "Text-to-speech allows dynamic content with template variables"
                                            }
                                            icon={formData.useAudioFile ? FileAudio : MessageSquare}
                                            checked={formData.useAudioFile}
                                            onCheckedChange={handleUseAudioFileChange}
                                            disabled={isReadOnly}
                                        />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm p-3">
                                    <div className="space-y-2">
                                        <div>
                                            <p className="text-xs font-medium">Text-to-Speech:</p>
                                            <p className="text-xs text-muted-foreground">
                                                Write a message that will be converted to speech. Supports template variables like {`{{company}}`}, {`{{phone}}`}, {`{{message}}`}.
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium">Audio File:</p>
                                            <p className="text-xs text-muted-foreground">
                                                Provide a URL to a pre-recorded .mp3 or .wav file for consistent quality and pronunciation.
                                            </p>
                                        </div>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    {/* Message Content */}
                    <div className="space-y-2">
                        <Label 
                            htmlFor="messageContent"
                            className="flex items-center gap-2 text-sm font-medium"
                        >
                            {formData.useAudioFile ? (
                                <>
                                    <AudioLines className="h-4 w-4 text-primary" />
                                    Audio File URL
                                </>
                            ) : (
                                <>
                                    <MessageSquare className="h-4 w-4 text-primary" />
                                    Voicemail Message
                                </>
                            )}
                        </Label>
                        {formData.useAudioFile ? (
                            <>
                                <Input
                                    id="messageContent"
                                    type="url"
                                    className="h-9"
                                    placeholder="https://example.com/voicemail.mp3"
                                    value={formData.messageContent}
                                    onChange={(e) => handleMessageContentChange(e.target.value)}
                                    disabled={isReadOnly}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Provide a publicly accessible URL to your pre-recorded audio file (.mp3 or .wav format)
                                </p>
                            </>
                        ) : (
                            <>
                                <Textarea
                                    id="messageContent"
                                    className="min-h-[120px] resize-none"
                                    placeholder="Hi, this is {{company}}. {{message}}. Please call us back at {{phone}}."
                                    value={formData.messageContent}
                                    onChange={(e) => handleMessageContentChange(e.target.value)}
                                    disabled={isReadOnly}
                                />
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground">
                                        Available template variables: {`{{company}}`}, {`{{phone}}`}, {`{{message}}`}
                                    </p>
                                    <div className="p-3 rounded-md bg-muted/50 border">
                                        <p className="text-xs font-medium mb-2">Example messages:</p>
                                        <ul className="text-xs text-muted-foreground space-y-1.5">
                                            <li className="flex gap-2">
                                                <span className="text-muted-foreground/50">•</span>
                                                <span>"Hi, this is {`{{company}}`}. {`{{message}}`}. Please call us back at {`{{phone}}`}."</span>
                                            </li>
                                            <li className="flex gap-2">
                                                <span className="text-muted-foreground/50">•</span>
                                                <span>"Hello, this is {`{{company}}`}. We tried to reach you regarding {`{{message}}`}. Our callback number is {`{{phone}}`}."</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* URL validation warning */}
                    {formData.useAudioFile && formData.messageContent && !contentIsUrl && (
                        <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    <p className="text-sm font-medium mb-1">Invalid URL Format</p>
                                    <p className="text-xs">
                                        Please enter a valid URL starting with <code className="px-1.5 py-0.5 rounded bg-destructive/20 font-mono">http://</code> or <code className="px-1.5 py-0.5 rounded bg-destructive/20 font-mono">https://</code>
                                    </p>
                                    <p className="text-xs mt-1">
                                        Example: <span className="font-mono">https://example.com/voicemail.mp3</span>
                                    </p>
                                </AlertDescription>
                            </Alert>
                        </motion.div>
                    )}
                </CardContent>
            </Card>

            {/* Best Practices Card */}
            <Card className="shadow-sm border-dashed">
                <CardHeader className="border-b bg-muted/20 py-2.5">
                    <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                            <Lightbulb className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-sm font-medium leading-none">Best Practices</h4>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-2">
                    <ul className="text-xs text-muted-foreground space-y-1.5">
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Keep voicemail messages under 30 seconds</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Clearly state your name, company, and purpose</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Include a clear callback number or next steps</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Test your message before using in production</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>For phone numbers in text messages, spell them out: "four one five, five five five, one two three four"</span>
                        </li>
                    </ul>
                </CardContent>
            </Card>
        </motion.div>
    );
}
